import { AzureAuthenticator } from 'tis-azure-auth';
import { AzureAdClient, AzureAdEmailClient, ApiGateway } from '../config/config'
import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser'
import EventBus from '../utils/EventBus'

let Authenticator = undefined
export const getAuthenticator = () => {
  if ( !Authenticator ) {
    Authenticator = new AzureAuthenticator({azureClientId: AzureAdClient.info.clientId, azureTenantId: AzureAdClient.info.tenantId})
  }
  return Authenticator
}

let token = undefined
export const getToken = async (_cacheOnly = false) => {
  if (_cacheOnly) return token
  
  if (token) {
    // to reduce traffic ensure token is a good distance from expiration but not too fresh
    const expirationTime = token.idTokenClaims?.exp ? new Date(token.idTokenClaims.exp * 1000).getTime() : 0
    const currentTime = new Date().getTime()
    if ((expirationTime - currentTime) / 1000 / 60 < 30) {
      token = undefined
    }
  }
  if (!token) {
    const auth = getAuthenticator()
    try {
      token = await auth.authenticate()
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        console.error(`InteractionRequiredAuthError caught and clearing session for retry`)
        sessionStorage.clear()
        token = await getToken()
      } else throw (e)
    }
  }
  EventBus.emit('token', token)
  return token
}

export const getRequestConfig = async (options = {}) => {
  const {
    includeApimSubscriptionKey = false,
  } = options
  const token = await getToken()
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token.idToken}`,
    'x-api-key': ApiGateway.apiKey
  }


  if (includeApimSubscriptionKey) {
    headers['Ocp-Apim-Subscription-Key'] = ApiGateway.apiKey
  }

  return {
    headers
  }
}

export function getHostUrl() {
  return `${[window.location.protocol,'//',
  window.location.hostname,
  window.location.hostname === 'localhost' ? [':',window.location.port].join('') : ''].join('')}`
}

export function logout() {
  getAuthenticator().logout()
}

const EMAIL_POPUP_SCOPES = ['openid', 'profile', 'User.Read', 'Mail.Send']
let emailMsalClient = undefined

const getEmailMsalClient = () => {
  if (!emailMsalClient) {
    emailMsalClient = new PublicClientApplication({
      auth: {
        clientId: AzureAdEmailClient.info.clientId,
        authority: `https://login.microsoftonline.com/${AzureAdEmailClient.info.tenantId}`,
        redirectUri: getHostUrl(),
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    })
  }
  return emailMsalClient
}

export const getEmailPopupAccessToken = async (options = {}) => {
  const { interactive = true, forceNetwork = false } = options
  const msalClient = getEmailMsalClient()

  if (typeof msalClient.initialize === 'function') {
    await msalClient.initialize()
  }

  let account = msalClient.getActiveAccount()
  if (!account) {
    const accounts = msalClient.getAllAccounts()
    account =
      accounts.find((a) => a.tenantId === AzureAdEmailClient.info.tenantId) ||
      accounts[0]
    if (account) {
      msalClient.setActiveAccount(account)
    }
  }

  if (!account) {
    const cachedToken = await getToken(true)
    const loginHint =
      cachedToken?.idTokenClaims?.preferred_username ||
      cachedToken?.idTokenClaims?.upn ||
      cachedToken?.idTokenClaims?.email ||
      cachedToken?.idTokenClaims?.mail

    try {
      const response = await msalClient.ssoSilent({
        scopes: EMAIL_POPUP_SCOPES,
        loginHint,
      })
      account = response?.account
      if (account) {
        msalClient.setActiveAccount(account)
      }
    } catch (err) {
      if (!interactive) {
        return undefined
      }
      // fall through to popup acquisition
    }
  }

  if (account) {
    try {
      const silentResponse = await msalClient.acquireTokenSilent({
        account,
        scopes: EMAIL_POPUP_SCOPES,
        forceRefresh: forceNetwork,
      })
      return silentResponse?.accessToken
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError)) {
        throw err
      }

      if (!interactive) {
        return undefined
      }
    }
  }

  if (!interactive) {
    return undefined
  }

  const popupResponse = await msalClient.acquireTokenPopup({
    scopes: EMAIL_POPUP_SCOPES,
    prompt: 'select_account',
  })
  if (popupResponse?.account) {
    msalClient.setActiveAccount(popupResponse.account)
  }
  return popupResponse?.accessToken
}
